import { type MouseEvent, memo } from 'react';

import { PlayingCardsFanIcon } from '@air/design-icons';

import { WorkspaceAgentGuidedVariantSizeGroupToggle } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeGroupToggle';
import { WorkspaceAgentGuidedVariantSizeOptionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSizeOptionRow';
import { WorkspaceAgentGuidedVariantSmartResizeFlatChannelGroup } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSmartResizeFlatChannelGroup';
import { WorkspaceAgentGuidedVariantSmartResizeShapeGroup } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSmartResizeShapeGroup';
import { WorkspaceAgentGuidedVariantSmartResizeSocialGroup } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSmartResizeSocialGroup';
import {
  isWorkspaceAgentSmartResizeSizeSelected,
  sizeSetIsFullySelected,
  WORKSPACE_AGENT_SMART_RESIZE_ECOMMERCE_GROUP,
  WORKSPACE_AGENT_SMART_RESIZE_LIFECYCLE_GROUP,
  type WorkspaceAgentSmartResizeSavedSize,
  type WorkspaceAgentSmartResizeSize,
  type WorkspaceAgentSmartResizeSizeSet,
  workspaceAgentSmartResizeSavedSizeToOptionRowChannel,
  workspaceAgentSmartResizeSizeSetToOptionRowChannel,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentSmartResize';

export type WorkspaceAgentGuidedVariantSmartResizeBrowseProps = {
  expandedGroups: ReadonlySet<string>;
  isExactMode: boolean;
  isSavedCatalogLoading: boolean;
  onExactSize: () => void;
  onSelectSet: (event: MouseEvent<HTMLButtonElement>) => void;
  onSelectSize: (event: MouseEvent<HTMLButtonElement>) => void;
  onToggleGroup: (event: MouseEvent<HTMLButtonElement>) => void;
  savedSets: readonly WorkspaceAgentSmartResizeSizeSet[];
  savedSizes: readonly WorkspaceAgentSmartResizeSavedSize[];
  sizes: readonly WorkspaceAgentSmartResizeSize[];
};

export const WorkspaceAgentGuidedVariantSmartResizeBrowse = memo(
  ({
    expandedGroups,
    isExactMode,
    isSavedCatalogLoading,
    onExactSize,
    onSelectSet,
    onSelectSize,
    onToggleGroup,
    savedSets,
    savedSizes,
    sizes,
  }: WorkspaceAgentGuidedVariantSmartResizeBrowseProps) => (
    <fieldset aria-label="Channels" className="m-0 flex min-w-0 flex-col border-0 p-0">
      <p className="px-1.5 py-2 font-bold ads:text-metadata text-10 text-gray-10 uppercase tracking-wide">Channels</p>
      <div>
        <WorkspaceAgentGuidedVariantSizeGroupToggle
          groupId="saved"
          isExpanded={expandedGroups.has('saved')}
          label="Saved"
          onToggle={onToggleGroup}
        />
        {expandedGroups.has('saved') ? (
          <div className="flex flex-col pl-6">
            {savedSets.length === 0 && savedSizes.length === 0 && !isSavedCatalogLoading ? (
              <p className="px-1.5 py-1.5 ads:text-metadata text-14 text-gray-9">
                Pick a few, then use Save set below.
              </p>
            ) : null}
            {savedSets.map((sizeSet) => {
              const { channel, meta } = workspaceAgentSmartResizeSizeSetToOptionRowChannel(sizeSet);
              const isSelected = sizeSetIsFullySelected(sizes, sizeSet);

              return (
                <WorkspaceAgentGuidedVariantSizeOptionRow
                  channel={channel}
                  data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SET"
                  isSelected={isSelected}
                  key={sizeSet.id}
                  leading={
                    <PlayingCardsFanIcon aria-hidden className="shrink-0 ads:text-icon-subtle text-gray-9" size="sm" />
                  }
                  meta={meta}
                  onClick={onSelectSet}
                  setId={sizeSet.id}
                />
              );
            })}
            {savedSizes.map((saved) => {
              const { channel, meta } = workspaceAgentSmartResizeSavedSizeToOptionRowChannel(saved);
              const isSelected = isWorkspaceAgentSmartResizeSizeSelected(sizes, saved.size);

              return (
                <WorkspaceAgentGuidedVariantSizeOptionRow
                  channel={channel}
                  isSelected={isSelected}
                  key={saved.id}
                  meta={meta}
                  onClick={onSelectSize}
                />
              );
            })}
          </div>
        ) : null}
      </div>
      <WorkspaceAgentGuidedVariantSmartResizeFlatChannelGroup
        group={WORKSPACE_AGENT_SMART_RESIZE_ECOMMERCE_GROUP}
        isExpanded={expandedGroups.has(WORKSPACE_AGENT_SMART_RESIZE_ECOMMERCE_GROUP.id)}
        onSelectSize={onSelectSize}
        onToggle={onToggleGroup}
        sizes={sizes}
      />
      <WorkspaceAgentGuidedVariantSmartResizeSocialGroup
        isExpanded={expandedGroups.has('social')}
        onSelectSize={onSelectSize}
        onToggle={onToggleGroup}
        sizes={sizes}
      />
      <WorkspaceAgentGuidedVariantSmartResizeFlatChannelGroup
        group={WORKSPACE_AGENT_SMART_RESIZE_LIFECYCLE_GROUP}
        isExpanded={expandedGroups.has(WORKSPACE_AGENT_SMART_RESIZE_LIFECYCLE_GROUP.id)}
        onSelectSize={onSelectSize}
        onToggle={onToggleGroup}
        sizes={sizes}
      />
      <WorkspaceAgentGuidedVariantSmartResizeShapeGroup
        isExactMode={isExactMode}
        isExpanded={expandedGroups.has('shape')}
        onExactSize={onExactSize}
        onSelectSize={onSelectSize}
        onToggle={onToggleGroup}
        sizes={sizes}
      />
    </fieldset>
  ),
);

WorkspaceAgentGuidedVariantSmartResizeBrowse.displayName = 'WorkspaceAgentGuidedVariantSmartResizeBrowse';
